import type { GameItem } from '../../i18n'
import { shuffle } from '../utils'

export type GameMode =
  | 'images-images'
  | 'words-words'
  | 'images-words'

export type Difficulty = 'easy' | 'medium' | 'hard'

export type CardDisplayType = 'emoji' | 'image' | 'word' | 'definition'

export interface Card {
  id:          number
  pairId:      number
  displayType: CardDisplayType
  content:     string   // emoji char, image URL, word text, or definition text
  altText?:    string   // accessibility label for image cards
  isFlipped:   boolean
  isMatched:   boolean
}

export const DIFFICULTY_PAIRS: Record<Difficulty, number> = {
  easy: 6,
  medium: 8,
  hard: 12,
}

export const MODE_ICONS: Record<GameMode, { iconA: string; iconB: string }> = {
  'images-images': { iconA: '🖼️', iconB: '🖼️' },
  'words-words':   { iconA: '📝', iconB: '📝' },
  'images-words':  { iconA: '🖼️', iconB: '📝' },
}

/* ── Card generation ─────────────────────────────────────────── */
export function generateCards(mode: GameMode, difficulty: Difficulty, items: GameItem[]): Card[] {
  const pairs = DIFFICULTY_PAIRS[difficulty]
  const selected = shuffle(items).slice(0, pairs)

  const cardPairs: [Card, Card][] = selected.map((item) => {
    const base = { pairId: item.id, isFlipped: false, isMatched: false }

    // When the item has an imageUrl (from an image theme) use 'image' display;
    // otherwise fall back to the classic emoji.
    const visualType: CardDisplayType = item.imageUrl ? 'image' : 'emoji'
    const visualContent = item.imageUrl ?? item.emoji
    const visualAlt = item.imageUrl ? item.word : undefined

    switch (mode) {
      case 'images-images':
        return [
          { ...base, id: 0, displayType: visualType, content: visualContent, altText: visualAlt },
          { ...base, id: 0, displayType: visualType, content: visualContent, altText: visualAlt },
        ]
      case 'words-words':
        return [
          { ...base, id: 0, displayType: 'word', content: item.word },
          { ...base, id: 0, displayType: 'word', content: item.word },
        ]
      case 'images-words':
        return [
          { ...base, id: 0, displayType: visualType, content: visualContent, altText: visualAlt },
          { ...base, id: 0, displayType: 'word',     content: item.word },
        ]
    }
  })

  const flat = shuffle(cardPairs.flat())
  return flat.map((card, i) => ({ ...card, id: i }))
}
